import { Avatar } from 'components/Avatar';
import { Button } from 'components/Button';
import { Grid, GridProps } from 'components/Grid';
import { Icon, ICONS } from 'components/Icon';
import { Page } from 'components/Page';
import { PageEmpty } from 'components/PageEmpty';
import { Txt } from 'components/Txt';
import { Pill } from 'components/Pill';
import { pluralise } from 'lib/pluralise';
import { theme } from 'lib/theme';
import React from 'react';
import { StyleSheet, View } from 'react-native';
import { useDispatch } from 'react-redux';
import {
  doEngageFlatLoad,
  doEngageFlatsUnload,
  doEngageFlatsLoad,
  doEngageOnboard,
} from 'store/remote/engageActions';
import { NAVIGATE_MODAL } from 'store/actions';
import {
  selectEngageFlatsFilterSearch,
  selectEngageFlatsItem,
  selectEngageFlatsItemIds,
  selectEngageFlatsTotalCount,
  selectEngageIsLoadedEver,
} from 'store/remote/engageSelectors';
import { useSelector } from 'hooks/useSelector';
import { EngageLoader } from './EngageLoader';
import { escapeRegExp } from 'lodash';
import { EngageFlatsHeader } from './EngageFlatsHeader';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import {
  ModalScreens,
  NavigationStacks,
  Screens,
  Stacks,
} from 'components/Navigation/types';
import { EngageFlatListItem, FlatOccupationStatus } from 'types/Engage';
import { filterArrayEmpty } from 'lib/riskbase';

type Props = NativeStackScreenProps<
  NavigationStacks[Stacks.Engage],
  Screens.EngageFlats
>;

type DefinedGridProps = GridProps<
  'flat' | 'contactTypes' | 'issues' | 'contactHasSupportNeeds',
  string,
  {
    pageKey: string;
    onRowPress: (property: EngageFlatListItem) => void;
  }
>;

const getContactNameWithHighlights = (
  contact: EngageFlatListItem['contacts'][0],
  highlightSelection: string,
  isMatchingContact: boolean,
) => {
  if (!isMatchingContact) {
    return (
      <Txt ellipsizeMode="tail" size="xsmall">
        {contact.name}
      </Txt>
    );
  }

  const emailContainsSearchTerm = contact.email
    .toLowerCase()
    .includes(highlightSelection.toLowerCase());
  const phoneContainsSearchTerm = contact.phone
    .toLowerCase()
    .includes(highlightSelection.toLowerCase());
  const nameContainsSearchTerm = contact.name
    .toLowerCase()
    .includes(highlightSelection.toLowerCase());

  const prefixIcons = [
    emailContainsSearchTerm && !nameContainsSearchTerm && (
      <Icon source={ICONS.AtLight} size={12} style={styles.matchingIconStyle} />
    ),
    phoneContainsSearchTerm && !nameContainsSearchTerm && (
      <Icon
        source={ICONS.PhoneSolid}
        size={12}
        style={styles.matchingIconStyle}
      />
    ),
  ];

  if (nameContainsSearchTerm) {
    const escapedSearchTerm = escapeRegExp(highlightSelection);
    const parts = contact.name.split(
      RegExp(
        `((?<=${escapedSearchTerm.toLowerCase()})|(?=${escapedSearchTerm.toLowerCase()}))`,
        'i',
      ),
    );

    return (
      <>
        {prefixIcons}
        <Txt size="xsmall" ellipsizeMode="tail">
          {parts.map((part, index) => {
            const partMatchesSearch =
              part.toLowerCase() === highlightSelection.toLowerCase();

            return (
              <Txt
                key={`${contact.id}-${index}`}
                ellipsizeMode="tail"
                weight="bold"
                size="xsmall"
                style={
                  partMatchesSearch ? styles.matchingContactItem : undefined
                }
              >
                {part}
              </Txt>
            );
          })}
        </Txt>
      </>
    );
  }

  return (
    <>
      {prefixIcons}
      <Txt ellipsizeMode="tail" size="xsmall">
        {contact.name}
      </Txt>
    </>
  );
};

const getDetailsWithHighlights = (text: string, highlightSelection: string) => {
  if (text.toLowerCase().includes(highlightSelection.toLowerCase())) {
    const escapedSearchTerm = escapeRegExp(highlightSelection);
    const parts = text.split(
      RegExp(`((?<=${escapedSearchTerm})|(?=${escapedSearchTerm}))`, 'i'),
    );

    return (
      <>
        {parts.map((part, index) => {
          if (part.toLowerCase() === highlightSelection.toLowerCase()) {
            return (
              <Txt
                key={index}
                style={styles.matchingContactItem}
                numberOfLines={1}
                weight="bold"
                size="large"
              >
                {part}
              </Txt>
            );
          }

          return (
            <Txt key={index} numberOfLines={1} weight="bold" size="large">
              {part}
            </Txt>
          );
        })}
      </>
    );
  }

  return text;
};

const useGetRowData: DefinedGridProps['getRowData'] = ({ id, extraData }) => {
  const { pageKey, onRowPress } = extraData;
  const data = useSelector((state) =>
    selectEngageFlatsItem(state, pageKey, id),
  );
  const searchValue = useSelector((state) =>
    selectEngageFlatsFilterSearch(state, pageKey),
  );
  const trimmedSearchValue = searchValue.trim();
  const matchingContactIds = React.useMemo(
    () => data?.matchingContactIds ?? [],
    [data?.matchingContactIds],
  );

  return React.useMemo(() => {
    if (!data) return null;

    return {
      onPress: () => onRowPress(data),
      columns: {
        flat: () => {
          return (
            <View style={styles.flatCell}>
              <Avatar
                data={data.imageUrl ? { remoteUrl: data.imageUrl } : null}
                size={50}
                resizeMode="cover"
                shape="square"
              />

              <View style={styles.flatTextWrap}>
                <Txt numberOfLines={1} weight="bold" size="large">
                  {trimmedSearchValue.length > 0
                    ? getDetailsWithHighlights(data.name, trimmedSearchValue)
                    : data.name}
                </Txt>
                {!!data.customReference && (
                  <Txt size="xsmall">{data.customReference}</Txt>
                )}
              </View>
            </View>
          );
        },
        contactTypes: () => {
          const ownerContacts = data.contacts.filter(
            (contact) => contact.isOwner,
          );
          const residentContacts = data.contacts.filter(
            (contact) => contact.isResident,
          );
          const summaryParts: string[] = [];
          // Add resident info
          if (data.occupationStatus === FlatOccupationStatus.UNKNOWN) {
            summaryParts.push('Residents Not Known');
          } else {
            summaryParts.push(
              pluralise(
                residentContacts.length,
                '{x} Resident',
                '{x} Residents',
                'Unoccupied',
              ),
            );
          }
          // Add owner info
          if (data.isCommerciallyOwned === 'YES') {
            summaryParts.push(
              pluralise(
                ownerContacts.length,
                '{x} Institutional Landlord',
                '{x} Institutional Landlords',
                'Institutional Landlord',
              ),
            );
          } else {
            summaryParts.push(
              pluralise(
                ownerContacts.length,
                '{x} Owner',
                '{x} Owners',
                'Owners Not Known',
              ),
            );
          }
          if (data.isHMO === 'YES') {
            summaryParts.push('HMO');
          }
          return (
            <View
              style={{
                flex: 1,
                justifyContent: 'center',
              }}
            >
              <Txt numberOfLines={1} weight="bold" size="xsmall">
                {summaryParts.join(', ')}
              </Txt>
              {data.contacts.length > 0 && (
                <View style={styles.residentNamesWrap}>
                  {data.contacts.map((contact, index) => (
                    <View key={contact.id} style={styles.contactNameItem}>
                      {getContactNameWithHighlights(
                        contact,
                        trimmedSearchValue,
                        matchingContactIds.includes(contact.id),
                      )}

                      {index < data.contacts.length - 1 && (
                        <Txt size="xxsmall">, </Txt>
                      )}
                    </View>
                  ))}
                </View>
              )}
            </View>
          );
        },
        issues: () => {
          const issueLabels = [
            data.issues.hasOwnerIssues ? 'Owner Issues' : null,
            data.issues.hasResidentIssues ? 'Resident Issues' : null,
            data.issues.hasEmailIssues ? 'Email Issues' : null,
            data.issues.hasSurveyResponseOverdue
              ? 'Survey Response Overdue'
              : null,
            data.issues.requiresContactSurvey ? 'Survey Required' : null,
          ].filter(filterArrayEmpty);
          return (
            <View style={styles.issueCell}>
              <View style={styles.issuePillsWrap}>
                {issueLabels.length ? (
                  issueLabels.map((label) => (
                    <Pill
                      key={label}
                      withIcon={true}
                      iconColor={theme.colors.red}
                      label={label}
                    />
                  ))
                ) : (
                  <Pill
                    withIcon={true}
                    iconColor={theme.colors.green}
                    label="No Issues"
                  />
                )}
              </View>
            </View>
          );
        },
        contactHasSupportNeeds: () => {
          const requiredSupportCount = data.contacts.filter(
            (contact) => contact.hasSupportNeeds && contact.isResident,
          ).length;
          return (
            <View
              style={{
                flex: 1,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <View
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 100,
                }}
              >
                <Txt
                  style={{
                    textAlign: 'center',
                  }}
                  weight={requiredSupportCount ? 'bold' : undefined}
                >
                  {requiredSupportCount}
                </Txt>
              </View>
            </View>
          );
        },
      },
    };
  }, [data, matchingContactIds, onRowPress, trimmedSearchValue]);
};

export const EngageFlats: React.FC<Props> = ({
  navigation,
  route: {
    params: { pageKey, property },
  },
}) => {
  const dispatch = useDispatch();

  const isLoadedEver = useSelector((state) =>
    selectEngageIsLoadedEver(state, pageKey, 'flats'),
  );
  const gridIds = useSelector((state) =>
    selectEngageFlatsItemIds(state, pageKey),
  );

  const totalCount = useSelector((state) =>
    selectEngageFlatsTotalCount(state, pageKey),
  );
  const remainingCount = totalCount - gridIds.length;
  const [selectedFlatIds, setSelectedFlatIds] = React.useState<string[]>([]);
  const hasBulkSelection = selectedFlatIds.length > 0;
  React.useEffect(() => {
    setSelectedFlatIds((current) =>
      current.filter((id) => gridIds.includes(id)),
    );
  }, [gridIds]);

  const [columns] = React.useState<DefinedGridProps['columns']>([
    {
      name: 'Flat',
      dataIndex: 'flat',
      width: 150,
    },
    {
      name: 'Contacts',
      dataIndex: 'contactTypes',
      minWidth: 310,
      flex: 2,
    },
    {
      name: 'Issues',
      dataIndex: 'issues',
      width: 200,
      textAlign: 'left',
    },
    {
      name: 'Evac Support',
      dataIndex: 'contactHasSupportNeeds',
      textAlign: 'left',
      width: 100,
    },
  ]);

  const handleLoadGrid = React.useCallback(
    (page: 'reset' | 'next') => {
      dispatch(
        doEngageFlatsLoad({
          propertyId: property.id,
          key: pageKey,
          page,
        }),
      );
    },
    [dispatch, pageKey, property.id],
  );

  const handleLoadMorePress = () => {
    handleLoadGrid('next');
  };

  const handleReloadPress = () => {
    handleLoadGrid('reset');
  };

  const handleOnboardFlatsPress = () => {
    dispatch(doEngageOnboard({ key: pageKey, propertyId: property.id }));
  };

  const handleScheduledVisitsPress = () => {
    navigation.navigate(Screens.EngageScheduledVisits, {
      pageKey: pageKey,
      property: property,
    });
  };

  const handleExportCsvPress = () => {
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.EngageContactUpload,
        params: {
          key: pageKey,
          propertyId: property.id,
        },
      }),
    );
  };

  const handlePressBack = React.useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  const handleRowPress = React.useCallback(
    (flat: EngageFlatListItem) => {
      dispatch(
        doEngageFlatLoad({
          key: pageKey,
          property: property,
          flat: flat,
        }),
      );
    },
    [dispatch, pageKey, property],
  );

  const handleSelectionChange = React.useCallback((ids: string[]) => {
    setSelectedFlatIds(ids);
  }, []);

  const handleBulkSendPress = React.useCallback(() => {
    if (!selectedFlatIds.length) {
      return;
    }
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.EngageSendCommunication,
        params: {
          key: pageKey,
          propertyId: property.id,
          flatIds: selectedFlatIds,
          contactId: null,
          mode: selectedFlatIds.length === 1 ? 'singleFlat' : 'bulkFlat',
        },
      }),
    );
  }, [dispatch, pageKey, property, selectedFlatIds]);

  React.useEffect(() => {
    handleLoadGrid('reset');
    return () => {
      dispatch(doEngageFlatsUnload({ key: pageKey }));
    };
  }, [dispatch, pageKey, handleLoadGrid]);

  return (
    <View style={{ position: 'relative', flex: 1 }}>
      <Page
        header={
          <EngageFlatsHeader
            property={property}
            onBackPress={handlePressBack}
            onReloadPress={handleReloadPress}
            onOnboardFlatsPress={handleOnboardFlatsPress}
            onScheduledVisitsPress={handleScheduledVisitsPress}
            onExportCsvPress={handleExportCsvPress}
            flatsCount={totalCount}
            pageKey={pageKey}
            filtersLocked={hasBulkSelection}
            hasBulkSelection={hasBulkSelection}
            onBulkSendPress={handleBulkSendPress}
          />
        }
      >
        <EngageLoader pageKey={pageKey} section="flats" />
        {!isLoadedEver ? (
          <View />
        ) : !gridIds.length ? (
          <PageEmpty
            text={`No Flats Or Contacts Found In\n${property.name}`}
            button={{
              text: 'Onboard Flats',
              onPress: handleOnboardFlatsPress,
            }}
            iconCircled={false}
            style={{ backgroundColor: theme.colors.background }}
          />
        ) : (
          <Grid
            columns={columns}
            data={gridIds}
            showSelectCheckboxes={true}
            selectedIds={selectedFlatIds}
            onSelectionChange={handleSelectionChange}
            extraData={{
              pageKey: pageKey,
              onRowPress: handleRowPress,
            }}
            getRowData={useGetRowData}
            keyExtractor={(i) => `${i}`}
            ListFooterComponent={
              remainingCount > 0 ? (
                <View
                  style={{
                    padding: theme.padding * 2,
                    paddingTop: theme.padding,
                    alignItems: 'flex-start',
                  }}
                >
                  <Button
                    text={pluralise(
                      remainingCount,
                      '{x} More Result, Load',
                      '{x} More Results, Load More',
                    )}
                    onPress={handleLoadMorePress}
                    icon={ICONS.ArrowDownLight}
                  />
                </View>
              ) : (
                <View style={{ height: theme.padding * 2 }} />
              )
            }
          />
        )}
      </Page>
    </View>
  );
};

const styles = StyleSheet.create({
  flatCell: {
    flex: 1,
    flexDirection: 'row',
    gap: theme.padding,
  },
  flatTextWrap: {
    flex: 1,
    justifyContent: 'center',
  },

  issueCell: {
    flex: 1,
    justifyContent: 'center',
    alignSelf: 'stretch',
  },
  issuePillsWrap: {
    rowGap: 1,
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
  residentNamesWrap: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    marginTop: 2,
  },
  contactNameItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  matchingContactItem: {
    backgroundColor: theme.colors.yellowHighlight,
    color: theme.colors.black,
  },
  matchingIconStyle: {
    marginRight: 4,
    padding: 4,
    marginLeft: 4,
    transform: [{ translateY: 1 }, { translateX: 1 }],
    tintColor: theme.colors.black,
    borderRadius: 4,
  },
  pillStyle: {
    shadowColor: 'black',
    shadowOpacity: 0.1,
    shadowOffset: { width: 0, height: 2 },
    shadowRadius: 2,
    backgroundColor: theme.colors.background80,
    borderRadius: 8,
  },
});
